<template>
  <project-settings-layout :id="id" :name="name">
    <div class="project-settings-email-settings">
      <el-card>
        <template #header>
          <h3>Email Settings</h3>
        </template>
        <entity-settings-loading v-if="isLoading" />
        <template v-else>
          <p>
            Any email sent or forwarded to one of the addresses below creates a new task in this project.
          </p>
          <div class="dd-divider" />
          <div class="row">
            <div class="col-12 forwarding-emails-list">
              <div class="forwarding-emails-list__header">
                <h4>Email Addresses</h4>
                <dd-icon-button
                  :is-disabled="isSubmitting"
                  icon="plus"
                  type="circle"
                  size="medium"
                  title="Add new forwarding email"
                  @click="createEmailAlias()"
                />
              </div>
              <div v-if="hasEmailAliases">
                <project-settings-auto-forwarding-email
                  v-for="emailAlias in emailAliases"
                  :id="emailAlias.id"
                  :key="emailAlias.id"
                  :email="emailAlias.name"
                  :disabled="isSubmitting"
                  @remove="confirmRemoveEmailAlias(emailAlias)"
                />
              </div>
              <p v-else class="forwarding-emails-list__empty-message">
                There are no email addresses associated to this project.
                <a href="#" class="just-a-link" @click.stop.prevent="createEmailAlias()">Add your first</a>!
              </p>
            </div>
          </div>
          <p><br></p>
          <p class="small">
            Click an address to copy it to your clipboard. If an address has been
            compromised, remove it and add a new one. You will need to update the
            auto-forwarding rule on your existing forwarding addresses.
          </p>
        </template>
      </el-card>

      <!-- Initial task settings card -->
      <el-card class="task-settings-card">
        <template #header>
          <h3>Task Rules</h3>
        </template>
        <entity-settings-loading v-if="isLoading" />
        <template v-else>
          <h4>Initial Task Settings</h4>
          <p>Set a task’s settings when a forwarded email arrives in this project.</p>
          <p><br></p>
          <el-form :model="formData" :disabled="isSaving" label-position="top">
            <div class="row">
              <div class="col-lg-6">
                <el-form-item label="Status" prop="defaultStatusID">
                  <single-select v-model="formData.defaultStatusID" placeholder="Select your status">
                    <el-option v-for="item in settings.listStatuses" :key="item.id" :value="item.id" :label="item.name" />
                  </single-select>
                </el-form-item>
              </div>
              <div class="col-lg-6">
                <el-form-item label="Priority" prop="defaultPriorityID">
                  <single-select v-model="formData.defaultPriorityID" placeholder="Select your priority">
                    <el-option v-for="item in settings.listPriorities" :key="item.id" :value="item.id" :label="item.name" />
                  </single-select>
                </el-form-item>
              </div>
              <div class="col-lg-6">
                <el-form-item label="Assignee" prop="defaultAssigneeID">
                  <single-select v-model="formData.defaultAssigneeID" placeholder="Select your assignee">
                    <el-option v-for="item in settings.listAssignees" :key="item.id" :value="item.id" :label="item.name" />
                  </single-select>
                </el-form-item>
              </div>
              <div class="col-lg-6">
                <el-form-item label="Tags" prop="listDefaultTags">
                  <tags-input
                    v-model:selected-tags="formData.listDefaultTags"
                    :is-disabled="isSaving"
                    hide-icon
                    disable-selection
                    show-input-border
                  />
                </el-form-item>
              </div>
            </div>
          </el-form>

          <dd-button
            :is-loading="isSaving"
            :color="ColorAssistant.ProjectColor"
            size="large"
            class="float-right"
            @click="saveEmailSettings"
          >Save Changes</dd-button>

          <div class="clearfix" />
        </template>
      </el-card>
    </div>
  </project-settings-layout>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import ProjectSettingsLayout from '@/layouts/project-settings-layout.vue'

/* Mixins */

/* Components */
import EntitySettingsLoading from '@/layouts/entity-settings-loading.vue'
import ProjectSettingsAutoForwardingEmail from './components/project-settings-auto-forwarding-email.vue'

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import {
  IItemForSelection,
  IInternalProjectsOutputIncomingEmailSettings,
} from '@/api/types/response.types'

/* API */
import InternalProjectsAPI from '@/api/modules/internal-projects.api'
import { IInternalProjectsInputIncomingEmailSettings } from '@/api/types/request.types'

export default defineComponent({
  name: 'ProjectSettingsEmailSettings',

  components: {
    ProjectSettingsLayout,
    ProjectSettingsAutoForwardingEmail,
    EntitySettingsLoading,
  },

  data() {
    return {
      ColorAssistant,
      isLoading: true,
      isSubmitting: false,
      isSaving: false,
      name: '',
      emailAliases: [] as IItemForSelection[],
      settings: {} as IInternalProjectsOutputIncomingEmailSettings,
      formData: {} as IInternalProjectsInputIncomingEmailSettings,
    }
  },

  head(): any {
    return {
      title: `Email Settings - ${this.name}`,
    }
  },

  computed: {
    id(): number {
      return parseInt(this.$route.params.id, 10)
    },
    hasEmailAliases(): boolean {
      return this.emailAliases.length > 0
    },
  },

  async created() {
    this.isLoading = true
    await this.getAutoForwardingInfo()
    this.isLoading = false
  },

  methods: {
    async getAutoForwardingInfo(): Promise<void> {
      try {
        const [settings, emailAliases] = await Promise.all([
          InternalProjectsAPI.get_IncomingEmailSettings(this.id),
          InternalProjectsAPI.get_EmailAliasGet(this.id),
        ])
        this.name = settings.name
        this.settings = settings
        this.emailAliases = emailAliases
        this.formData = {
          defaultAssigneeID: settings.defaultAssigneeID,
          defaultStatusID: settings.defaultStatusID,
          defaultPriorityID: settings.defaultPriorityID,
          listDefaultTags: settings.listDefaultTags,
        } as IInternalProjectsInputIncomingEmailSettings
      } catch (error) {
        this.$error.emit(error)
      }
    },
    async saveEmailSettings(): Promise<void> {
      try {
        this.isSaving = true
        const response = await InternalProjectsAPI.post_IncomingEmailSettings(
          this.id,
          this.formData,
        )
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
        await this.getAutoForwardingInfo()
      } finally {
        this.isSaving = false
      }
    },
    async createEmailAlias(): Promise<void> {
      try {
        this.isSubmitting = true
        const emailAlias = await InternalProjectsAPI.post_EmailAliasCreate(
          this.id,
        )
        this.emailAliases.push(emailAlias)
        this.$messaging.showSuccess(
          'A new forwarding address has been created!',
        )
      } catch (error) {
        this.$error.emit(error, true)
        await this.getAutoForwardingInfo()
      } finally {
        this.isSubmitting = false
      }
    },
    confirmRemoveEmailAlias(emailAlias: IItemForSelection) {
      this.$confirm(
        `Removing ${emailAlias.name} does not disable auto-forwarding. You will
        also need to remove the auto-forwarding rule from your email provider.`,
        'Warning!',
        {
          confirmButtonText: 'I understand, remove this email.',
          cancelButtonText: 'Cancel',
          confirmButtonClass: 'el-button--strawberry',
          type: 'warning',
          center: true,
        },
      ).then(() => {
        this.deleteEmailAlias(emailAlias.id)
      })
    },
    async deleteEmailAlias(emailAliasID: number): Promise<void> {
      try {
        this.isSubmitting = true
        const response = await InternalProjectsAPI.delete_EmailAliasDelete(
          this.id,
          emailAliasID,
        )
        this.emailAliases = this.emailAliases.filter(a => a.id !== emailAliasID)
        this.$messaging.showSuccess(response.message)
      } catch (error) {
        this.$error.emit(error, true)
        await this.getAutoForwardingInfo()
      } finally {
        this.isSubmitting = false
      }
    },
  },
})
</script>

<style lang="scss">
.project-settings-email-settings {
  margin-bottom: 20px;

  .forwarding-emails-list {
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
    }

    &__empty-message {
      color: var(--color-text-third);
      padding-bottom: 20px;
    }
  }

  .task-settings-card {
    margin-top: 24px;
    overflow: visible;
  }
}
</style>
